import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, ChevronDown, HelpCircle, Brain, RefreshCw } from "lucide-react";
import api, { errMsg } from "@/lib/api";

export const AiTicketBadge = ({ testId }) => (
  <span data-testid={testId} title="Opened by the AI Agent after the user confirmed"
    className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-300 border border-violet-400/30 shrink-0">
    <Sparkles className="w-3 h-3" /> Opened by AI
  </span>
);

export const AiConversationPanel = ({ convo }) => {
  const [open, setOpen] = useState(true);
  return (
    <div data-testid="ticket-ai-conversation" className="px-4 py-3 border-b border-zinc-800 bg-violet-500/[0.04]">
      <button data-testid="ticket-ai-conversation-toggle" onClick={() => setOpen(!open)}
        className="w-full flex flex-wrap items-center gap-x-2 gap-y-1 text-left text-xs text-violet-300 font-medium">
        <Sparkles className="w-3.5 h-3.5" /> {convo.via === "ai_dm" ? "DM chat that led to this ticket" : "AI conversation that led to this ticket"}
        {convo.asked_at && <span className="text-zinc-500 font-normal">· {new Date(convo.asked_at).toLocaleString()}</span>}
        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-2 text-sm">
          {convo.history?.length > 0 ? (
            <div data-testid="ticket-ai-history" className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {convo.history.map((h, i) => (
                <div key={i} data-testid={`ticket-ai-history-${i}`} className={`flex ${h.role === "user" ? "" : "justify-end"}`}>
                  <p className={`max-w-[85%] rounded-lg px-3 py-2 whitespace-pre-wrap break-words ${h.role === "user" ? "bg-zinc-800 text-zinc-100" : "bg-violet-500/10 border border-violet-400/20 text-zinc-300"}`}>
                    <span className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">{h.role === "user" ? "User" : "AI"}</span>{h.text}
                  </p>
                </div>
              ))}
            </div>
          ) : <>
          <p data-testid="ticket-ai-question" className="text-zinc-200 flex gap-2"><HelpCircle className="w-4 h-4 shrink-0 mt-0.5 text-zinc-500" />{convo.question}</p>
          <p data-testid="ticket-ai-answer" className="text-zinc-400 whitespace-pre-wrap break-words pl-6 border-l border-violet-400/30">{convo.answer}</p>
          </>}
          {convo.tools_used?.length > 0 && (
            <div className="pl-6 flex flex-wrap gap-1.5" data-testid="ticket-ai-tools">
              {convo.tools_used.map((t, i) => (
                <span key={i} className={`text-[11px] border rounded px-1.5 py-0.5 ${t.ok ? "border-sky-400/30 text-sky-300" : "border-red-400/30 text-red-400"}`}>
                  {t.name.replace(/^get_/, "")} · {t.username || t.user_id}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const AiSummaryCard = ({ guildId, ticket, onUpdated }) => {
  const [busy, setBusy] = useState(false);
  const s = ticket.ai_summary;
  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/guilds/${guildId}/tickets/${ticket.id}/ai-summary/regenerate`);
      toast.success(data.posted_to_discord ? "Summary regenerated and posted to the Discord ticket" : "Summary regenerated");
      onUpdated();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="ticket-ai-summary" className="px-4 py-3 border-b border-zinc-800 bg-volt/[0.04]">
      <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-volt">
        <Brain className="w-3.5 h-3.5" /> AI Summary for staff
        {s?.generated_at && <span className="text-zinc-500 font-normal">· {s.regenerated ? "regenerated" : "generated"} {new Date(s.generated_at).toLocaleString()}</span>}
        <button data-testid="ticket-ai-summary-regenerate" onClick={regenerate} disabled={busy}
          className="ml-auto flex items-center gap-1 text-zinc-300 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 rounded-md px-2.5 py-1 transition-colors">
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> {busy ? "Regenerating…" : "Regenerate"}
        </button>
      </div>
      {s?.text
        ? <div data-testid="ticket-ai-summary-text" className="mt-2 text-sm text-zinc-200 whitespace-pre-wrap break-words">{s.text}</div>
        : <p data-testid="ticket-ai-summary-pending" className="mt-2 text-sm text-zinc-500">Generating summary… (refreshes automatically)</p>}
    </div>
  );
};
